import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Plus, Trash, DownloadSimple, PaperPlaneTilt, CheckCircle, XCircle,
  PencilSimple, ClockCounterClockwise, Warning, ChatCircleText, Paperclip,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import ModalPortal from "@/components/ModalPortal";
import AtaAttachments from "@/components/AtaAttachments";

/* ---------- utilities ---------- */

const TYPE_LABEL = {
  addition: "Tillägg",
  change: "Ändring",
  deduction: "Avgående",
  delay: "Hinder / väntetid",
};

const STATUS_LABEL = {
  draft: "Utkast",
  sent: "Skickad",
  approved: "Godkänd",
  rejected: "Nekad",
};

const CATEGORY_LABEL = {
  labor: "Arbete",
  material: "Material",
  markup: "Påslag",
  other: "Övrigt",
};

function fmtKr(n) {
  const val = Number(n || 0);
  return `${val.toLocaleString("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("sv-SE");
  } catch { return iso; }
}

function StatusPill({ status }) {
  const cls = {
    draft: "tk-pill",
    sent: "tk-pill tk-pill-warning",
    approved: "tk-pill tk-pill-active",
    rejected: "tk-pill text-rust",
  }[status] || "tk-pill";
  return <span className={`${cls} text-[10px]`}>{STATUS_LABEL[status] || status}</span>;
}

/* ---------- Main modal ---------- */

export default function AtaModal({ object, onClose }) {
  const [tab, setTab] = useState("list"); // list | summary
  const [atas, setAtas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // ata object or {_new:true} for create
  const [viewing, setViewing] = useState(null); // ata for read-only view
  const [contractSumDraft, setContractSumDraft] = useState("");
  const [savingContractSum, setSavingContractSum] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get(`/ata/objects/${object.object_id}/list`),
        api.get(`/ata/objects/${object.object_id}/summary`),
      ]);
      setAtas(l.data || []);
      setSummary(s.data || null);
      setContractSumDraft(String(s.data?.original_contract_sum || ""));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte hämta ÄTA-data");
    } finally {
      setLoading(false);
    }
  }, [object.object_id]);

  useEffect(() => { load(); }, [load]);

  const saveContractSum = async () => {
    const val = parseFloat(contractSumDraft.replace(/\s/g, "").replace(",", "."));
    if (Number.isNaN(val) || val < 0) {
      toast.error("Ange ett giltigt belopp");
      return;
    }
    setSavingContractSum(true);
    try {
      await api.patch(`/ata/objects/${object.object_id}/contract-sum`, {
        original_contract_sum: val,
      });
      toast.success("Kontraktsumma sparad");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSavingContractSum(false);
    }
  };

  const downloadPdf = async (ata) => {
    try {
      const r = await api.get(`/ata/${ata.ata_id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${ata.ata_number}-${object.name || "objekt"}.pdf`.replace(/[\\/:*?"<>|]+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "PDF-export misslyckades");
    }
  };

  const sendForApproval = async (ata) => {
    // Föreslå e-post i denna prioritet:
    // 1. ÄTA:ns egen beställare_email (om användaren fyllt i)
    // 2. Objektets client_email (kund-e-post på Objektet)
    const suggested = ata.beställare_email || object?.client_email || "";
    const email = window.prompt(
      `Ange beställarens e-post för digital signering:`,
      suggested,
    );
    if (!email) return;
    try {
      const r = await api.post(`/ata/${ata.ata_id}/send`, { recipient_email: email });
      toast.success(`ÄTA skickad till ${email}`);
      // Kopiera länk till urklipp så användaren också kan skicka manuellt
      if (r.data.signing_url && navigator.clipboard) {
        navigator.clipboard.writeText(r.data.signing_url).catch(() => {});
        toast.info("Signeringslänk kopierad till urklipp");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte skicka");
    }
  };

  const deleteAta = async (ata) => {
    if (!window.confirm(`Radera ${ata.ata_number} · ${ata.title}?`)) return;
    try {
      await api.delete(`/ata/${ata.ata_id}`);
      toast.success("ÄTA raderad");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte radera");
    }
  };

  return (
    <ModalPortal>
      <div
        data-testid="ata-modal"
        className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-surface border border-app rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-app flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-text-muted">ÄTA-hantering</div>
              <h2 className="font-heading text-2xl md:text-3xl truncate">{object.name}</h2>
              <div className="text-xs text-text-muted mt-1">
                {object.address || "Ingen adress"}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid="new-ata-button"
                onClick={() => setEditing({ _new: true })}
                className="tk-btn-primary inline-flex items-center gap-2"
              >
                <Plus size={16} /> Ny ÄTA
              </button>
              <button
                onClick={onClose}
                className="text-text-muted hover:text-text-primary p-2"
                aria-label="Stäng"
              >
                <X size={22} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-4 border-b border-app flex gap-1">
            {[
              { key: "list", label: `ÄTA-lista (${atas.length})` },
              { key: "summary", label: "Sammanställning" },
            ].map((t) => (
              <button
                key={t.key}
                data-testid={`ata-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                  tab === t.key
                    ? "border-[var(--brand-rust)] text-text-primary font-medium"
                    : "border-transparent text-text-muted hover:text-text-primary"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <p className="text-center text-text-muted py-12 text-sm">Laddar…</p>
            ) : tab === "list" ? (
              <AtaList
                atas={atas}
                onEdit={(a) => setEditing(a)}
                onView={(a) => setViewing(a)}
                onDelete={deleteAta}
                onSend={sendForApproval}
                onPdf={downloadPdf}
              />
            ) : (
              <AtaSummary
                summary={summary}
                contractSumDraft={contractSumDraft}
                setContractSumDraft={setContractSumDraft}
                onSave={saveContractSum}
                saving={savingContractSum}
              />
            )}
          </div>
        </div>
      </div>

      {editing && (
        <AtaForm
          object={object}
          ata={editing._new ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {viewing && (
        <AtaViewModal
          ata={viewing}
          onClose={() => setViewing(null)}
          onPdf={() => downloadPdf(viewing)}
        />
      )}
    </ModalPortal>
  );
}

/* ---------- List ---------- */

function AtaList({ atas, onEdit, onView, onDelete, onSend, onPdf }) {
  if (!atas.length) {
    return (
      <p className="text-center text-text-muted py-10 text-sm" data-testid="ata-list-empty">
        Inga ÄTA:er skapade ännu. Klicka på <strong>Ny ÄTA</strong> för att skapa den första.
      </p>
    );
  }
  return (
    <div className="tk-card p-0 overflow-hidden" data-testid="ata-list">
      <div className="hidden md:grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-text-muted border-b border-app">
        <div className="col-span-2">Nr</div>
        <div className="col-span-4">Titel</div>
        <div className="col-span-2">Typ</div>
        <div className="col-span-2 text-right">Belopp</div>
        <div className="col-span-2 text-right">Status</div>
      </div>
      {atas.map((a) => (
        <div
          key={a.ata_id}
          data-testid={`ata-row-${a.ata_id}`}
          className="grid grid-cols-12 px-4 py-3 border-b border-app last:border-0 items-center gap-y-2"
        >
          <div className="col-span-6 md:col-span-2 font-mono text-sm">{a.ata_number}</div>
          <div className="col-span-6 md:col-span-4">
            <div className="font-medium truncate">{a.title}</div>
            <div className="text-xs text-text-muted truncate">
              {a.beställare_company || "—"} · {fmtDate(a.created_at)}
            </div>
          </div>
          <div className="col-span-4 md:col-span-2 text-sm">{TYPE_LABEL[a.ata_type] || a.ata_type}</div>
          <div className="col-span-4 md:col-span-2 text-right font-mono text-sm tabular-nums">
            {fmtKr(a.totals?.total_excl_vat)}
          </div>
          <div className="col-span-4 md:col-span-2 flex justify-end items-center gap-2 flex-wrap">
            <StatusPill status={a.status} />
          </div>

          {/* Actions */}
          <div className="col-span-12 flex flex-wrap gap-1 justify-end mt-1">
            <button
              data-testid={`ata-view-${a.ata_id}`}
              onClick={() => onView(a)}
              className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
            >
              <ChatCircleText size={13} /> Detaljer
            </button>
            {a.status !== "approved" && a.status !== "rejected" && (
              <button
                data-testid={`ata-edit-${a.ata_id}`}
                onClick={() => onEdit(a)}
                className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
              >
                <PencilSimple size={13} /> Redigera
              </button>
            )}
            {a.status === "draft" && (
              <button
                data-testid={`ata-send-${a.ata_id}`}
                onClick={() => onSend(a)}
                className="tk-btn-primary !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
              >
                <PaperPlaneTilt size={13} /> Skicka
              </button>
            )}
            {a.status === "sent" && (
              <button
                data-testid={`ata-resend-${a.ata_id}`}
                onClick={() => onSend(a)}
                className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
              >
                <PaperPlaneTilt size={13} /> Skicka igen
              </button>
            )}
            <button
              data-testid={`ata-pdf-${a.ata_id}`}
              onClick={() => onPdf(a)}
              className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
            >
              <DownloadSimple size={13} /> PDF
            </button>
            {a.status !== "approved" && (
              <button
                data-testid={`ata-delete-${a.ata_id}`}
                onClick={() => onDelete(a)}
                className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1 text-rust"
              >
                <Trash size={13} /> Radera
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Summary ---------- */

function AtaSummary({ summary, contractSumDraft, setContractSumDraft, onSave, saving }) {
  if (!summary) return null;
  const {
    original_contract_sum, approved_sum, sent_sum, rejected_sum, draft_sum,
    current_project_sum, approved_delta_pct, counts_by_status,
  } = summary;

  return (
    <div className="space-y-6">
      <div className="tk-card p-5">
        <div className="text-xs uppercase tracking-wider text-text-muted">Ursprunglig kontraktsumma</div>
        <div className="mt-2 flex gap-2 items-end">
          <div className="flex-1">
            <input
              data-testid="contract-sum-input"
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={contractSumDraft}
              onChange={(e) => setContractSumDraft(e.target.value)}
              className="tk-input w-full font-mono text-lg"
            />
          </div>
          <button
            data-testid="save-contract-sum-button"
            onClick={onSave}
            disabled={saving}
            className="tk-btn-primary"
          >
            {saving ? "Sparar…" : "Spara"}
          </button>
        </div>
        <p className="text-xs text-text-muted mt-1">
          Ange summan enligt ursprunglig offert / kontrakt exkl. moms. Används för att visa ÄTA-påverkan i procent.
        </p>
      </div>

      <div className="tk-card p-5 space-y-3" data-testid="ata-summary">
        <SummaryRow label="Ursprunglig kontraktsumma" value={fmtKr(original_contract_sum)} />
        <SummaryRow label={`Godkända ÄTA (${counts_by_status.approved} st)`} value={fmtKr(approved_sum)} pct={original_contract_sum > 0 ? `${approved_delta_pct > 0 ? "+" : ""}${approved_delta_pct}%` : null} highlight />
        <SummaryRow label={`Skickade (ej svar) (${counts_by_status.sent} st)`} value={fmtKr(sent_sum)} muted />
        <SummaryRow label={`Nekade (${counts_by_status.rejected} st)`} value={fmtKr(rejected_sum)} muted strike />
        <SummaryRow label={`Utkast (${counts_by_status.draft} st)`} value={fmtKr(draft_sum)} muted />
        <div className="pt-3 border-t border-app">
          <SummaryRow label="Aktuell projektsumma (godkända)" value={fmtKr(current_project_sum)} bold />
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, pct, bold, highlight, muted, strike }) {
  return (
    <div className={`flex items-center justify-between text-sm ${muted ? "text-text-muted" : ""}`}>
      <span>{label}</span>
      <span className={`font-mono tabular-nums ${bold ? "font-bold text-lg" : ""} ${highlight ? "text-[var(--brand-forest)]" : ""} ${strike ? "line-through" : ""}`}>
        {value}{pct ? <span className="ml-2 text-xs">({pct})</span> : null}
      </span>
    </div>
  );
}

/* ---------- Create / edit form ---------- */

function AtaForm({ object, ata, onClose, onSaved }) {
  const editing = !!ata;
  const [form, setForm] = useState(() => ({
    ata_type: ata?.ata_type || "addition",
    title: ata?.title || "",
    description: ata?.description || "",
    reason: ata?.reason || "",
    // Vid ny ÄTA: ärv från Objektets kunduppgifter så användaren slipper skriva om det.
    // Vid redigering: använd ÄTA:ns egna värden (som redan innehåller det som var ärvt vid create).
    beställare_company: ata?.beställare_company ?? (editing ? "" : (object?.client || "")),
    beställare_org_number: ata?.beställare_org_number ?? (editing ? "" : (object?.client_org_number || "")),
    beställare_name: ata?.beställare_name ?? (editing ? "" : (object?.client_contact_name || "")),
    beställare_email: ata?.beställare_email ?? (editing ? "" : (object?.client_email || "")),
    contract_reference: ata?.contract_reference ?? (editing ? "" : (object?.contract_reference || "")),
    affects_completion: !!ata?.affects_completion,
    completion_delay_days: ata?.completion_delay_days || 0,
    line_items: ata?.line_items?.length
      ? ata.line_items
      : [{ category: "labor", description: "", quantity: 1, unit: "tim", unit_price: 0 }],
  }));
  const [saving, setSaving] = useState(false);

  const total = form.line_items.reduce(
    (s, li) => s + (Number(li.quantity || 0) * Number(li.unit_price || 0)),
    0,
  );

  const updateLine = (idx, patch) => {
    const next = [...form.line_items];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, line_items: next });
  };

  const addLine = () => {
    setForm({
      ...form,
      line_items: [
        ...form.line_items,
        { category: "labor", description: "", quantity: 1, unit: "tim", unit_price: 0 },
      ],
    });
  };

  const removeLine = (idx) => {
    if (form.line_items.length === 1) return;
    setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Ange en titel");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.map((li) => ({
          category: li.category,
          description: li.description.trim(),
          quantity: Number(li.quantity) || 0,
          unit: li.unit || null,
          unit_price: Number(li.unit_price) || 0,
        })).filter((li) => li.description),
      };
      if (editing) {
        await api.patch(`/ata/${ata.ata_id}`, payload);
        toast.success("ÄTA uppdaterad");
      } else {
        await api.post(`/ata/objects/${object.object_id}`, payload);
        toast.success("ÄTA skapad");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalPortal>
      <div
        data-testid="ata-form-modal"
        className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
        onClick={() => !saving && onClose()}
      >
        <div
          className="bg-surface border border-app rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-app flex items-center justify-between">
            <h2 className="font-heading text-2xl">
              {editing ? `Redigera ${ata.ata_number}` : "Ny ÄTA"}
            </h2>
            <button onClick={() => !saving && onClose()} className="text-text-muted hover:text-text-primary" aria-label="Stäng">
              <X size={22} />
            </button>
          </div>

          <form onSubmit={submit} className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Typ */}
            <div>
              <label className="text-xs text-text-muted">Typ av arbete</label>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="ata-type-selector">
                {["addition", "change", "deduction", "delay"].map((t) => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`ata-type-${t}`}
                    onClick={() => setForm({ ...form, ata_type: t })}
                    className={`px-3 py-2 rounded-md border text-sm transition ${
                      form.ata_type === t
                        ? "border-[var(--brand-rust)] bg-[var(--brand-rust)]/10 text-text-primary font-medium"
                        : "border-app text-text-muted hover:border-[var(--brand-rust)]/40"
                    }`}
                  >
                    {TYPE_LABEL[t]}
                  </button>
                ))}
              </div>
            </div>

            {/* Titel + beskrivning */}
            <div>
              <label className="text-xs text-text-muted">Titel *</label>
              <input
                data-testid="ata-title-input"
                required maxLength={120}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Extra eluttag i badrum vån 2"
                className="tk-input w-full"
              />
            </div>

            <div>
              <label className="text-xs text-text-muted">Beskrivning av arbetet</label>
              <textarea
                data-testid="ata-description-input"
                maxLength={2000}
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Detaljerad beskrivning av det arbete som utförs..."
                className="tk-input w-full"
              />
            </div>

            <div>
              <label className="text-xs text-text-muted">Beställare / orsak</label>
              <input
                data-testid="ata-reason-input"
                maxLength={500}
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder='Ex: "Beställd av byggchef Anna Svensson via telefon 2026-02-10"'
                className="tk-input w-full"
              />
            </div>

            {/* Beställare-block */}
            <div className="border border-app rounded-md p-4 space-y-3">
              <div className="text-xs uppercase tracking-wider text-text-muted">Beställare</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-text-muted">Företag</label>
                  <input
                    data-testid="ata-company-input"
                    maxLength={120}
                    value={form.beställare_company}
                    onChange={(e) => setForm({ ...form, beställare_company: e.target.value })}
                    className="tk-input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted">Org.nr</label>
                  <input
                    data-testid="ata-orgnr-input"
                    maxLength={30}
                    value={form.beställare_org_number}
                    onChange={(e) => setForm({ ...form, beställare_org_number: e.target.value })}
                    className="tk-input w-full font-mono"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted">Kontaktperson</label>
                  <input
                    data-testid="ata-contact-input"
                    maxLength={120}
                    value={form.beställare_name}
                    onChange={(e) => setForm({ ...form, beställare_name: e.target.value })}
                    className="tk-input w-full"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-muted">E-post (för signering)</label>
                  <input
                    data-testid="ata-email-input"
                    type="email"
                    maxLength={200}
                    value={form.beställare_email}
                    onChange={(e) => setForm({ ...form, beställare_email: e.target.value })}
                    className="tk-input w-full"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-text-muted">Kontraktsreferens (t.ex. AB 04 kap 2 § 4)</label>
                <input
                  data-testid="ata-contract-ref-input"
                  maxLength={200}
                  value={form.contract_reference}
                  onChange={(e) => setForm({ ...form, contract_reference: e.target.value })}
                  className="tk-input w-full"
                />
              </div>
            </div>

            {/* Ekonomirader */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-text-muted">Specifikation</div>
                <button
                  type="button"
                  data-testid="add-line-item-button"
                  onClick={addLine}
                  className="tk-btn-outline !py-1.5 !px-3 text-xs inline-flex items-center gap-1"
                >
                  <Plus size={13} /> Lägg till rad
                </button>
              </div>
              <div className="space-y-2" data-testid="line-items">
                {form.line_items.map((li, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`line-item-${idx}`}>
                    <select
                      value={li.category}
                      onChange={(e) => updateLine(idx, { category: e.target.value })}
                      className="tk-input col-span-3 md:col-span-2 text-sm"
                    >
                      {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                        <option key={v} value={v}>{l}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Beskrivning"
                      value={li.description}
                      onChange={(e) => updateLine(idx, { description: e.target.value })}
                      className="tk-input col-span-9 md:col-span-4 text-sm"
                    />
                    <input
                      type="number" step="0.01" min="0"
                      placeholder="Antal"
                      value={li.quantity}
                      onChange={(e) => updateLine(idx, { quantity: e.target.value })}
                      className="tk-input col-span-3 md:col-span-1 text-sm font-mono"
                    />
                    <input
                      type="text"
                      placeholder="Enh."
                      maxLength={20}
                      value={li.unit || ""}
                      onChange={(e) => updateLine(idx, { unit: e.target.value })}
                      className="tk-input col-span-3 md:col-span-1 text-sm"
                    />
                    <input
                      type="number" step="0.01" min="0"
                      placeholder="Á-pris"
                      value={li.unit_price}
                      onChange={(e) => updateLine(idx, { unit_price: e.target.value })}
                      className="tk-input col-span-4 md:col-span-2 text-sm font-mono"
                    />
                    <div className="col-span-1 md:col-span-1 text-right font-mono text-sm tabular-nums pt-2">
                      {fmtKr((Number(li.quantity) || 0) * (Number(li.unit_price) || 0))}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      disabled={form.line_items.length === 1}
                      className="col-span-1 text-text-muted hover:text-rust disabled:opacity-30 pt-2"
                      aria-label="Ta bort rad"
                    >
                      <Trash size={16} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-app flex items-center justify-between">
                <span className="text-sm text-text-muted">Totalt exkl. moms</span>
                <span className="font-mono text-lg font-bold" data-testid="ata-total">{fmtKr(total)}</span>
              </div>
            </div>

            {/* Tidspåverkan */}
            <div className="border border-app rounded-md p-4 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="ata-affects-completion"
                  checked={form.affects_completion}
                  onChange={(e) => setForm({ ...form, affects_completion: e.target.checked })}
                />
                Påverkar färdigställandet
              </label>
              {form.affects_completion && (
                <div className="flex items-center gap-2 pl-6 text-sm">
                  Förlängning med
                  <input
                    type="number" min={0} max={365}
                    data-testid="ata-delay-days"
                    value={form.completion_delay_days}
                    onChange={(e) => setForm({ ...form, completion_delay_days: Number(e.target.value) })}
                    className="tk-input w-24 font-mono"
                  />
                  dagar
                </div>
              )}
            </div>

            {/* Foto- och filbilagor */}
            <div className="border border-app rounded-md p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Paperclip size={16} className="text-text-muted" />
                <span className="text-xs uppercase tracking-wider text-text-muted">
                  Foton & bilagor
                </span>
              </div>
              {editing ? (
                <AtaAttachments ataId={ata.ata_id} canEdit />
              ) : (
                <p className="text-xs text-text-muted">
                  Spara ÄTA:n först — sen kan du ta foto och bifoga filer.
                </p>
              )}
            </div>
          </form>

          <div className="p-4 border-t border-app flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="tk-btn-outline"
            >
              Avbryt
            </button>
            <button
              type="submit"
              data-testid="save-ata-button"
              onClick={submit}
              disabled={saving || !form.title.trim()}
              className="tk-btn-primary"
            >
              {saving ? "Sparar…" : editing ? "Spara ändringar" : "Skapa ÄTA"}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

/* ---------- View-only detail modal ---------- */

function AtaViewModal({ ata, onClose, onPdf }) {
  return (
    <ModalPortal>
      <div
        data-testid="ata-view-modal"
        className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <div
          className="bg-surface border border-app rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 border-b border-app flex items-start justify-between">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wider text-text-muted flex gap-2 items-center">
                {ata.ata_number} · {TYPE_LABEL[ata.ata_type]} <StatusPill status={ata.status} />
              </div>
              <h2 className="font-heading text-2xl mt-1">{ata.title}</h2>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={onPdf}
                className="tk-btn-outline !py-2 !px-3 text-xs inline-flex items-center gap-1"
              >
                <DownloadSimple size={14} /> PDF
              </button>
              <button onClick={onClose} className="text-text-muted hover:text-text-primary p-2" aria-label="Stäng">
                <X size={22} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4 text-sm">
            {ata.description && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Beskrivning</div>
                <p className="whitespace-pre-wrap">{ata.description}</p>
              </div>
            )}
            {ata.reason && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Beställare / orsak</div>
                <p className="whitespace-pre-wrap">{ata.reason}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Beställare</div>
                <div>{ata.beställare_company || "—"}</div>
                {ata.beställare_org_number && <div className="text-xs text-text-muted font-mono">Org.nr {ata.beställare_org_number}</div>}
                {ata.beställare_name && <div className="text-xs">{ata.beställare_name}</div>}
                {ata.beställare_email && <div className="text-xs text-text-muted">{ata.beställare_email}</div>}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-text-muted mb-1">Tidspåverkan</div>
                {ata.affects_completion ? (
                  <div className="inline-flex items-center gap-1 text-[var(--brand-rust)]">
                    <Warning size={14} /> Förlänger med {ata.completion_delay_days} dagar
                  </div>
                ) : (
                  <div className="text-text-muted">Påverkar ej slutdatum</div>
                )}
                {ata.contract_reference && (
                  <div className="text-xs text-text-muted mt-2">Ref: {ata.contract_reference}</div>
                )}
              </div>
            </div>

            {/* Ekonomirader */}
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Specifikation</div>
              <div className="tk-card p-0 overflow-hidden">
                {(ata.line_items || []).map((li, idx) => (
                  <div key={idx} className="grid grid-cols-12 px-3 py-2 border-b border-app last:border-0 text-xs items-center">
                    <div className="col-span-2">{CATEGORY_LABEL[li.category]}</div>
                    <div className="col-span-5 truncate">{li.description}</div>
                    <div className="col-span-2 text-right font-mono">{li.quantity} {li.unit}</div>
                    <div className="col-span-3 text-right font-mono">{fmtKr(li.quantity * li.unit_price)}</div>
                  </div>
                ))}
                <div className="grid grid-cols-12 px-3 py-2 items-center text-sm border-t-2 border-[var(--brand-rust)]/40">
                  <div className="col-span-9 font-medium">Totalt exkl. moms</div>
                  <div className="col-span-3 text-right font-mono font-bold">{fmtKr(ata.totals?.total_excl_vat)}</div>
                </div>
              </div>
            </div>

            {/* Bilagor (read-only view) */}
            <div>
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2 flex items-center gap-2">
                <Paperclip size={13} /> Bilagor
              </div>
              <AtaAttachments
                ataId={ata.ata_id}
                canEdit={ata.status !== "approved" && ata.status !== "rejected"}
              />
            </div>

            {/* Status-info */}
            {ata.status === "approved" && (
              <div className="tk-card p-3 border-l-4 border-[var(--brand-forest)] inline-flex items-center gap-2 text-sm">
                <CheckCircle size={18} weight="fill" className="text-[var(--brand-forest)]" />
                Godkänd digitalt {fmtDate(ata.decided_at)} från IP {ata.decision_ip || "—"}
              </div>
            )}
            {ata.status === "rejected" && (
              <div className="tk-card p-3 border-l-4 border-rust inline-flex items-start gap-2 text-sm">
                <XCircle size={18} weight="fill" className="text-rust" />
                <div>
                  <div>Nekad {fmtDate(ata.decided_at)}</div>
                  {ata.decision_reason && <div className="text-xs text-text-muted mt-1">Skäl: {ata.decision_reason}</div>}
                </div>
              </div>
            )}
            {ata.status === "sent" && (
              <div className="tk-card p-3 border-l-4 border-[var(--brand-rust)] inline-flex items-center gap-2 text-sm">
                <ClockCounterClockwise size={18} />
                Skickad för godkännande {fmtDate(ata.sent_at)}
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
